import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  ReviewItem,
  aggregateSeverityTint,
  compareReviewItems,
  summarizeSeverities,
} from "../lib/reviewItems";
import { ReviewItemRow } from "./ReviewItemRow";
import { t } from "../i18n";

// Handle exposed to parent via ref (same pattern as AudioPlayerHandle).
// expandAndFocusItem(startMs): expand the panel (if collapsed) and scroll +
// briefly highlight the first pending item whose time range covers startMs.
export interface ReviewPanelHandle {
  expandAndFocusItem: (startMs: number) => void;
}

interface Props {
  items: ReviewItem[];
  recordedAt: string | null;
  isLocked: boolean;
  // Drives empty-state distinguishing. 'ready' = transcription finished
  // (zero items here means either Claude scans never ran, or ran-and-clean).
  // Anything else = worker still in flight or errored; copy adapts.
  meetingStatus: string;
  meetingId: string;
  workspaceId: string;
  // Live signal for "have Claude annotation passes ever run for this meeting?"
  // True when attribution_synopsis is present and non-empty (the synopsis is
  // written by the new attribution pass and is null for genuinely-never-
  // annotated meetings or pre-pipeline legacy rows). Also OR'd with
  // span_attributions.length > 0 for extra robustness.
  // Replaces the dead hasSpeakerMap heuristic (speaker_map is always {} under
  // the current attribution pipeline, so hasSpeakerMap was always false).
  hasAnnotationRun: boolean;
  onSeek: (ms: number) => void;
  getOriginalText: (startMs: number, endMs: number) => string;
  // Edit affordance for claude_speaker proposals: parent maps a
  // start_ms to a turn index and opens the inline SpeakerOverride
  // popover. See ReviewItemRow for the per-kind action matrix.
  onEditSpeaker?: (startMs: number) => void;
  // Fired after a row's apply/undo DB write succeeds. Realtime is
  // unreliable in this codebase, so rows force an explicit refresh
  // through this callback (wired to MeetingReview's refreshAll) rather
  // than waiting for the postgres_changes echo.
  onActed?: () => void | Promise<void>;
  // Re-annotate CTA wiring. Parent owns the dialog state + the in-flight
  // gating; panel just fires onReAnnotate when the user clicks the
  // empty-state CTA. canReAnnotate guards visibility (status-driven;
  // matches the sticky title row's canReAnnotate(status) gate).
  onReAnnotate?: () => void;
  canReAnnotate: boolean;
  reAnnotating: boolean;
}

// Unified ReviewPanel. Item 2 of the 2026-05-14 architecture extension.
// Replaces FlagPanel + DriftBanner + the inline-only L3 surface. Same
// in-document slot family as the prior panels (between proposed cards
// and the AudioPlayer). Storage stays distributed for v0; the SPA
// aggregates at render time via aggregateReviewItems.
//
// Decisions pinned 2026-05-14 (DECISIONS.md "Semantic review surface
// + context-enrichment architecture" entry + Item 2 design-decisions):
//   - Single panel; no coexistence with old surfaces.
//   - Severity-desc ordering, chronological within tier (Q2).
//   - L3 items use opt-out semantics (Q3 (a)) -- inline render still
//     applies; the panel is the action surface for Accept/Dismiss/Edit.
//   - Empty/scanning/ran-clean rules carry over from FlagPanel.
//   - Auto-expand once on first high-severity sighting (Q10).
//   - Hidden when locked entirely; status preserved underneath (Q6).
//   - User-facing copy uses "Claude suggestion" (not "L3") per the
//     2026-05-14 UI-copy convention.
export const ReviewPanel = forwardRef<ReviewPanelHandle, Props>(function ReviewPanel({
  items,
  recordedAt,
  isLocked,
  meetingStatus,
  meetingId,
  workspaceId,
  hasAnnotationRun,
  onSeek,
  getOriginalText,
  onEditSpeaker,
  onActed,
  onReAnnotate,
  canReAnnotate,
  reAnnotating,
}, ref) {
  const [expanded, setExpanded] = useState(false);
  // Per-item DOM refs so expandAndFocusItem can scroll and flash the target.
  const itemRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  // Track pending highlight target: after expansion renders the list, find
  // and scroll to the item. Using a ref avoids a second state/effect cycle.
  const pendingFocusId = useRef<string | null>(null);

  // Auto-expand once on first high-severity sighting. Once the user
  // manually collapses, never re-opens (FlagPanel discipline carried
  // over to the unified panel).
  const [autoExpandedOnce, setAutoExpandedOnce] = useState(false);
  useEffect(() => {
    if (autoExpandedOnce) return;
    if (items.some((i) => i.severity === "high" && i.status !== "dismissed")) {
      setExpanded(true);
      setAutoExpandedOnce(true);
    }
  }, [items, autoExpandedOnce]);

  // After expansion, scroll+highlight any pending focus target.
  useEffect(() => {
    if (!expanded) return;
    const targetId = pendingFocusId.current;
    if (!targetId) return;
    pendingFocusId.current = null;
    const node = itemRefs.current.get(targetId);
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "nearest" });
    // Brief amber highlight then fade out.
    node.style.transition = "background-color 0.2s ease-out";
    node.style.backgroundColor = "rgba(180, 83, 9, 0.18)";
    const timer = setTimeout(() => {
      node.style.backgroundColor = "";
    }, 1200);
    return () => clearTimeout(timer);
  }, [expanded]);

  // Imperative handle: called by the flag-marker click in TurnRow.
  useImperativeHandle(ref, () => ({
    expandAndFocusItem(startMs: number) {
      // Find the first pending item whose range covers startMs.
      const target = items.find(
        (i) =>
          i.status === "pending" &&
          i.start_ms !== null &&
          i.end_ms !== null &&
          i.start_ms <= startMs &&
          i.end_ms > startMs,
      );
      const targetId = target?.id ?? null;
      pendingFocusId.current = targetId;

      if (!expanded) {
        // Setting expanded will trigger the useEffect above after render.
        setExpanded(true);
      } else if (targetId) {
        // Already expanded: scroll + highlight immediately.
        const node = itemRefs.current.get(targetId);
        if (node) {
          node.scrollIntoView({ behavior: "smooth", block: "nearest" });
          node.style.transition = "background-color 0.2s ease-out";
          node.style.backgroundColor = "rgba(180, 83, 9, 0.18)";
          setTimeout(() => {
            node.style.backgroundColor = "";
          }, 1200);
        }
        pendingFocusId.current = null;
      }
    },
  }), [items, expanded]);

  if (isLocked) return null;
  if (meetingStatus === "error") return null;

  const isScanning = ["pending", "uploaded", "transcribing", "annotating"].includes(
    meetingStatus,
  );

  // Hide entirely when scanning + 0 items. Status pill above already
  // tells the user annotations are in flight; an extra "scanning" card
  // adds noise.
  if (items.length === 0 && isScanning) {
    return null;
  }

  if (items.length === 0) {
    // status === 'ready' + empty. Two sub-cases:
    //   (a) hasAnnotationRun === false: Claude passes have never run — synopsis
    //       is null/empty. Common on meetings transcribed before the new
    //       attribution pipeline shipped, or when the worker errored before
    //       _run_annotations completed.
    //   (b) hasAnnotationRun === true: passes ran and found nothing to flag —
    //       a clean meeting. Show a positive all-clear, not a "nothing happened".
    return (
      <div
        style={{
          marginTop: 16,
          border: "1px solid var(--border, #374151)",
          borderRadius: 8,
          background: "var(--surface, #181b24)",
          padding: "12px 14px",
          fontSize: 13,
          color: "var(--text-muted)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "var(--text, #f9fafb)", fontWeight: 500, marginBottom: 4 }}>
            {hasAnnotationRun
              ? "The analysis reviewed this — nothing to flag."
              : "The analysis hasn't run on this transcript yet."}
          </div>
          <div style={{ fontSize: 12 }}>
            {hasAnnotationRun
              ? "Speaker attribution and mishearing checks completed with no items to address."
              : "Run the analysis: who said what with reasoning, an independent second check for swaps, and a sweep for suspicious mishearings."}
          </div>
        </div>
        {onReAnnotate && canReAnnotate && (
          <button
            type="button"
            className="secondary"
            disabled={reAnnotating}
            onClick={onReAnnotate}
            title="Re-run the analysis on the existing transcript."
            style={{ flexShrink: 0 }}
          >
            {reAnnotating ? t("journey.updating") : t("meeting.updateAnalysis")}
          </button>
        )}
      </div>
    );
  }

  const sorted = [...items].sort(compareReviewItems);
  const tint = aggregateSeverityTint(items);
  const breakdown = summarizeSeverities(items);

  return (
    <div
      style={{
        marginTop: 16,
        border: `1px solid ${tint}`,
        borderRadius: 8,
        background: "var(--surface, #181b24)",
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "10px 14px",
          background: "transparent",
          border: "none",
          color: "var(--text, #f9fafb)",
          cursor: "pointer",
          fontSize: 14,
          fontFamily: "inherit",
          textAlign: "left",
        }}
      >
        <span>
          <span style={{ color: tint, fontWeight: 600 }}>Review</span>
          <span style={{ marginLeft: 8, color: "var(--text-muted)" }}>
            — Claude's notes on this transcript · {items.length} item
            {items.length === 1 ? "" : "s"}
            {breakdown ? ` to address before lock (${breakdown})` : " to address before lock"}
          </span>
        </span>
        <span style={{ color: "var(--text-muted)", fontSize: 13 }}>
          {expanded ? "Hide details ▴" : "Show details ▾"}
        </span>
      </button>

      {expanded && (
        // ReviewItemRow already renders a <li>; we wrap each one in a <div>
        // so we can attach a ref for the flag-marker scroll+highlight without
        // creating nested <li> elements.
        <div style={{ padding: "0 14px 12px" }}>
          {sorted.map((item) => (
            <div
              key={item.id}
              ref={(node) => {
                itemRefs.current.set(item.id, node as HTMLDivElement | null);
              }}
              style={{ borderRadius: 4 }}
            >
              <ReviewItemRow
                item={item}
                recordedAt={recordedAt}
                isLocked={isLocked}
                meetingId={meetingId}
                workspaceId={workspaceId}
                onSeek={onSeek}
                getOriginalText={getOriginalText}
                onEditSpeaker={onEditSpeaker}
                onActed={onActed}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
});
